import React from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  STATS_COLUMN_KEYS,
  type ParamStats,
  type StatsColumnKey
} from '../../utils/passAnalyticsStats'
import { getParameterUnitSuffix } from '../../utils/weldUnitUtils'

/**
 * Human-readable label per stats column key. Mirrors the table column header
 * labels in `PassAnalyticsTable.makeColumns(...)`. Keep in sync if either
 * place adds a new metric.
 */
const STAT_KEY_LABELS: Record<StatsColumnKey, string> = {
  travelSpeed: 'Travel Speed',
  volts: 'Voltage',
  amps: 'Current',
  wireIpm: 'Wire Speed',
  widthMil: 'Oscillation Width',
  targetVlt: 'Target',
  csBias: 'Horizontal Bias',
  bpm: 'Frequency',
  totWireIn: 'Total Wire Consumed',
  trueEnergy: 'True Energy',
  heat: 'Heat'
}

const STAT_KEY_UNIT_PARAM: Record<StatsColumnKey, string> = {
  travelSpeed: 'travelSpeed',
  volts: 'volts',
  amps: 'amps',
  wireIpm: 'wireIpm',
  widthMil: 'widthMil',
  targetVlt: 'targetVlt',
  csBias: 'csBias',
  bpm: 'bpm',
  totWireIn: 'totWireIn',
  trueEnergy: 'trueEnergy',
  heat: 'rprtHeat'
}

export interface MinMaxConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  passLabel: string
  sampleCount: number
  totalSampleCount: number
  /** Live stats from the current sample selection (new values). */
  stats: Partial<Record<StatsColumnKey, ParamStats>>
  /** Last saved stats from the server (previous values). */
  previousStats: Partial<Record<StatsColumnKey, ParamStats>>
  /** When true, the Previous column shows a loading state. */
  previousLoading?: boolean
  busy?: boolean
}

function fmtStat(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const rounded = Math.abs(value - Math.round(value)) < 1e-9
  return rounded ? String(Math.round(value)) : Number(value).toFixed(2)
}

function statChanged(
  previous: number | null | undefined,
  next: number | null | undefined
): boolean {
  const prevMissing = previous === null || previous === undefined || !Number.isFinite(previous)
  const nextMissing = next === null || next === undefined || !Number.isFinite(next)
  if (prevMissing && nextMissing) return false
  if (prevMissing !== nextMissing) return true
  return previous !== next
}

/**
 * Confirmation dialog shown when the user clicks "Calculate min/max" on the
 * Pass Analytics page. Displays a per-parameter summary table of the values
 * that will be persisted, so the user can sanity-check before committing.
 *
 * Persistence happens on `onConfirm`; this component is presentation-only.
 */
export default function MinMaxConfirmDialog({
  open,
  onClose,
  onConfirm,
  passLabel,
  sampleCount,
  totalSampleCount,
  stats,
  previousStats,
  previousLoading = false,
  busy = false
}: MinMaxConfirmDialogProps) {
  if (!open) return null

  const rows = STATS_COLUMN_KEYS.map((key) => {
    const previous = previousStats[key]
    const next = stats[key]
    const suffix = getParameterUnitSuffix(STAT_KEY_UNIT_PARAM[key])
    return {
      key,
      label: STAT_KEY_LABELS[key],
      suffix,
      prevMin: previous?.min ?? null,
      prevAvg: previous?.avg ?? null,
      prevMax: previous?.max ?? null,
      newMin: next?.min ?? null,
      newAvg: next?.avg ?? null,
      newMax: next?.max ?? null
    }
  })

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-blueprint-blue/40 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="minmax-confirm-title"
      data-testid="minmax-confirm-dialog"
    >
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-large">
        <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-success-50 text-success-700 ring-1 ring-inset ring-success-100">
            <CheckCircleIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="minmax-confirm-title"
              className="font-heading text-lg font-semibold text-blueprint-blue"
            >
              Apply Min / Avg / Max for {passLabel}?
            </h2>
            <p className="text-xs text-gray-500">
              Computed from {sampleCount.toLocaleString()} of{' '}
              {totalSampleCount.toLocaleString()} samples. Previous shows values
              currently in Welding Parameters (or your last saved Recalculate).
              Confirming persists the new values for this pass.
            </p>
          </div>
          <button
            type="button"
            className="-mr-2 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-x-auto overflow-y-auto bg-page-bg/40 px-6 py-5">
          <table
            className="w-full min-w-[720px] border-collapse text-xs"
            data-testid="minmax-confirm-table"
          >
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th
                  rowSpan={2}
                  className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-heritage-grey"
                >
                  Parameter
                </th>
                <th
                  colSpan={3}
                  className="border-b border-l border-gray-200 px-3 py-2 text-center font-semibold uppercase tracking-wider text-heritage-grey"
                >
                  Previous
                </th>
                <th
                  colSpan={3}
                  className="border-b border-l border-gray-200 px-3 py-2 text-center font-semibold uppercase tracking-wider text-heritage-grey"
                >
                  New
                </th>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="w-20 border-l border-gray-200 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Min
                </th>
                <th className="w-20 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Avg
                </th>
                <th className="w-20 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Max
                </th>
                <th className="w-20 border-l border-gray-200 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Min
                </th>
                <th className="w-20 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Avg
                </th>
                <th className="w-20 px-3 py-2 text-right font-semibold uppercase tracking-wider text-heritage-grey">
                  Max
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  data-testid={`minmax-confirm-row-${row.key}`}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td className="px-3 py-2 text-left text-text-grey">
                    <span className="font-semibold">{row.label}</span>
                    {row.suffix ? (
                      <span className="ml-1 text-[10px] text-heritage-grey">
                        ({row.suffix})
                      </span>
                    ) : null}
                  </td>
                  <td
                    className={`border-l border-gray-100 px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevMin, row.newMin)
                        ? 'text-gray-500 line-through'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-prev-min-${row.key}`}
                  >
                    {previousLoading ? (
                      <span className="inline-block h-3 w-10 animate-pulse rounded bg-gray-200" />
                    ) : (
                      fmtStat(row.prevMin)
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevAvg, row.newAvg)
                        ? 'text-gray-500 line-through'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-prev-avg-${row.key}`}
                  >
                    {previousLoading ? (
                      <span className="inline-block h-3 w-10 animate-pulse rounded bg-gray-200" />
                    ) : (
                      fmtStat(row.prevAvg)
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevMax, row.newMax)
                        ? 'text-gray-500 line-through'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-prev-max-${row.key}`}
                  >
                    {previousLoading ? (
                      <span className="inline-block h-3 w-10 animate-pulse rounded bg-gray-200" />
                    ) : (
                      fmtStat(row.prevMax)
                    )}
                  </td>
                  <td
                    className={`border-l border-gray-100 px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevMin, row.newMin)
                        ? 'font-medium text-galvanizing-green'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-new-min-${row.key}`}
                  >
                    {fmtStat(row.newMin)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevAvg, row.newAvg)
                        ? 'font-medium text-galvanizing-green'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-new-avg-${row.key}`}
                  >
                    {fmtStat(row.newAvg)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      statChanged(row.prevMax, row.newMax)
                        ? 'font-medium text-galvanizing-green'
                        : 'text-text-grey'
                    }`}
                    data-testid={`minmax-confirm-new-max-${row.key}`}
                  >
                    {fmtStat(row.newMax)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3">
          <button
            type="button"
            data-testid="minmax-confirm-cancel"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="minmax-confirm-confirm"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-hero-red px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-hero-red-60 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onConfirm}
            disabled={busy || sampleCount === 0}
          >
            {busy ? 'Saving…' : 'Confirm & Save'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
